import { setAdminOrgId } from "@/shared/utils/admin-org-id";
import type { TOrgInfo } from "@/types";
import { useSearchAccountsMutation } from "@api/accounts";
import Input from "@components/ui/input";
import Popover from "@components/ui/popover";
import { ScrollArea } from "@components/ui/scroll-area";
import Spinner from "@components/ui/spinner";
import { useAppSelector } from "@store";
import { getAccount } from "@store/account/selectors";
import { useEffect, useRef, useState } from "react";
import { useDebounceValue } from "usehooks-ts";

export const UserMenuAccountSearch = () => {
  "use no memo";
  const account = useAppSelector(getAccount);
  const [searchAccounts, { isLoading, data }] = useSearchAccountsMutation();
  const [isFocused, setIsFocused] = useState(false);
  const [search, setSearch] = useState(account?.title || "");
  const [debouncedSearch] = useDebounceValue(search, 500);
  const hasSearchTriggered = useRef(false);
  const lastSearchTerm = useRef<string>("");

  const isTyping = search !== debouncedSearch;
  const isCurrentSearchPending = debouncedSearch !== lastSearchTerm.current;

  useEffect(() => {
    if (!debouncedSearch) return;
    if (!hasSearchTriggered.current) return;
    lastSearchTerm.current = debouncedSearch;
    searchAccounts({ search: debouncedSearch });
  }, [debouncedSearch, searchAccounts]);

  const isOpen =
    isFocused &&
    !isLoading &&
    !isTyping &&
    !isCurrentSearchPending &&
    !!debouncedSearch &&
    hasSearchTriggered.current &&
    data !== undefined;

  const handleSelect = (info: TOrgInfo) => {
    setAdminOrgId(info._id);
    updateSearchParams();
    window.location.href = "/pages/home";
  };

  return (
    <div>
      <Popover open={isOpen} onOpenChange={() => {}}>
        <Popover.Trigger>
          <div className="tw-relative tw-mb-1">
            <Input
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSearch(e.target.value);
                if (!hasSearchTriggered.current) {
                  hasSearchTriggered.current = true;
                }
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                // Allow space key to work normally in search input
                if (e.key === " ") {
                  e.stopPropagation();
                }
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              className="tw-h-9 tw-px-3 tw-pr-9"
              placeholder="Search accounts..."
            />
            {isLoading && (
              <div className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2">
                <Spinner className="tw-border-2" />
              </div>
            )}
          </div>
        </Popover.Trigger>
        <Popover.Content className="tw-w-[238px] tw-z-[99999]">
          <div className="tw-rounded-lg tw-bg-white tw-p-1 tw-shadow-md tw-border tw-border-blue/5 tw-border-solid">
            <SearchResults data={data} handleSelect={handleSelect} />
          </div>
        </Popover.Content>
      </Popover>
    </div>
  );
};

const SearchResults = ({
  data,
  handleSelect,
}: {
  data: TOrgInfo[];
  handleSelect: (info: TOrgInfo) => void;
}) => {
  if (!data?.length)
    return (
      <span className="tw-block tw-p-3 tw-text-center tw-text-[13px] tw-text-blue-500">
        No results found
      </span>
    );

  return (
    <ScrollArea maxHeight={250}>
      <div className="tw-space-y-0.5 tw-p-1">
        {data?.map((account: TOrgInfo) => (
          <button
            type="button"
            onClick={() => handleSelect(account)}
            key={account._id}
            className="tw-grid tw-cursor-pointer tw-w-full tw-rounded tw-px-2 tw-py-1 tw-text-sm tw-font-normal tw-text-blue tw-transition-colors hover:tw-bg-blue-50"
          >
            <span className="tw-truncate tw-text-left">{account.title}</span>
            <span className="tw-truncate tw-text-left tw-text-[10px]/none tw-text-blue-500">
              {account._id}
            </span>
          </button>
        ))}
      </div>
    </ScrollArea>
  );
};

const updateSearchParams = () => {
  const COOKIE_KEY = "sd-stories-filters";
  const current = localStorage.getItem(COOKIE_KEY);
  if (!current) return;
  const currentFilters = JSON.parse(current);
  const updated = { ...currentFilters, folder: undefined };
  localStorage.setItem(COOKIE_KEY, JSON.stringify(updated));
};
